import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import { pipe, Option } from 'effect';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import RouterLink from '@/components/mui/Router/RouterLink';
import ROUTES_PATH from '@/router/routesPathConfig';
import { companiesByIdSelector } from '@/domain/companies';
import { chatParticipantsSelector } from '@/domain/chat';

// Action types match chatActions.js. The syncAction creators are untyped JS
// (payload typed as `void`), so we dispatch raw action objects — the same
// pattern used in useChatChannelParam.ts / chatRedirectSaga.js.
const SET_ACTIVE_CHANNEL = 'CHAT/SET_ACTIVE_CHANNEL';
const SET_ACTIVE_CHANNEL_FORCE = 'CHAT/SET_ACTIVE_CHANNEL_FORCE';
const SET_OPEN = 'CHAT/SET_OPEN';

export interface ChatToastData {
  channelUrl: string;
  channelName: string;
  documentId: string | null;
  companyId: string;
  // Sender's userGUID (empty when the push omits it) — resolved against the
  // participant store to show the sender's avatar. See ChatToastEntry.
  senderUserGUID: string;
  senderName: string;
  messagePreview: string;
  label: string;
}

interface Props {
  toast: ChatToastData;
}

// Close button for the react-toastify toast (closeToast is injected by the
// container, same contract as the legacy ChatToastClose).
export const ChatToastClose: React.FC<{ closeToast?: () => void }> = ({ closeToast }) => {
  const { formatMessage } = useIntl();
  return (
    <IconButton
      onClick={closeToast}
      aria-label={formatMessage({ id: 'button.close', defaultMessage: 'Close' })}
      // `align-self: center` + `flex-shrink: 0` keep the button at its natural
      // square size. The toast row is `align-items: stretch` by default, which
      // otherwise stretches the button to the full (multi-line) toast height and
      // renders its circular hover highlight as a vertical oval (DA-19565).
      sx={{ mx: 0.5, alignSelf: 'center', flexShrink: 0 }}
    >
      <CloseIcon />
    </IconButton>
  );
};

/**
 * Chat v2 in-app notification toast.
 *
 * NOTE — this is intentionally SEPARATE from the legacy toast at
 * `Chat/legacy/components/ChatToast`, it is NOT a duplicate:
 *   - This v2 component takes a normalized `ChatToastData` (from the v2 push
 *     adapter `adaptOneSignalChatPush`); the legacy one takes a raw Sendbird
 *     `message` object and depends on v1-only state
 *     (`chatParticipantsByIdSelector`) + v1 helpers (`getParamsFromChannelName`,
 *     `sendbird-utils`). v2 code must not import from `legacy/`.
 *   - The two share only the visual pattern and the app-wide react-toastify host.
 *
 * Rendered inside a react-toastify `toast(...)` call from the v2 toast
 * middleware (the same mechanism legacy chat uses via notifyWithToast), so the
 * app-wide DraggableToastContainer owns stacking/dismissal/drag — no bespoke
 * host. Props come from the middleware's parsed push, so this component does no
 * payload parsing itself.
 */
const ChatToast: React.FC<Props> = ({ toast }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const companies = useSelector(companiesByIdSelector);
  const participants = useSelector(chatParticipantsSelector);

  // The push carries no avatar URL, so resolve the sender's picture from the
  // participant store. Chat v2 keys the store's `userGUID` to the Sendbird/push
  // sender.user_id (DA-19440) — match on that, NOT `userId` (which is the email).
  // Best-effort: the store is company-scoped, so the sender may be absent; the
  // Avatar then falls back to the sender's initial.
  const senderPicture = useMemo(
    () =>
      pipe(
        Option.fromNullable(
          toast.senderUserGUID
            ? participants.find((u: { get: (key: string) => string }) => u.get('userGUID') === toast.senderUserGUID)
            : undefined,
        ),
        Option.map((user) => user.get('picture')),
        Option.getOrElse(() => ''),
      ),
    [participants, toast.senderUserGUID],
  );
  const senderInitial = toast.senderName.trim().charAt(0).toUpperCase();

  // Read via Immutable getIn (company records are Immutable) — consistent with
  // the rest of the codebase and robust to Map-like shapes / test mocks.
  const companyName = toast.companyId ? companies.getIn([toast.companyId, 'cname']) : undefined;
  const conversation =
    toast.label || formatMessage({ id: 'chat.newMessage', defaultMessage: 'New message' });
  // Keep the legacy "sender · company" title signal.
  const title = toast.senderName
    ? companyName
      ? `${toast.senderName} · ${companyName}`
      : toast.senderName
    : companyName
      ? `${conversation} · ${companyName}`
      : conversation;

  const openConversation = () => {
    // Document toast → force-select the channel (survives unmount) + open drawer;
    // the RouterLink handles navigation. Channel toast → select + open.
    dispatch({
      type: toast.documentId ? SET_ACTIVE_CHANNEL_FORCE : SET_ACTIVE_CHANNEL,
      payload: { name: toast.channelUrl, companyId: toast.companyId },
    });
    dispatch({ type: SET_OPEN, payload: true });
  };

  const documentPath =
    toast.documentId && toast.companyId
      ? generatePath(ROUTES_PATH.DOCUMENT.absolute, {
          companyId: toast.companyId,
          documentId: toast.documentId,
        })
      : null;

  return (
    <Box display="flex" alignItems="center">
      <Avatar
        src={senderPicture || undefined}
        alt={toast.senderName || undefined}
        sx={{ width: 32, height: 32, marginInlineStart: 1, fontSize: 14 }}
      >
        {senderInitial || undefined}
      </Avatar>
      <List disablePadding>
        <ListItem>
          <ListItemText disableTypography slotProps={{ primary: { sx: { color: 'text.primary' } } }}>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {title}
            </Typography>
            {toast.messagePreview ? (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                {toast.messagePreview}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {formatMessage({ id: 'chat.newMessage', defaultMessage: 'New message' })}
              </Typography>
            )}
            {documentPath ? (
              <Link
                component={RouterLink}
                href={{ pathname: documentPath }}
                onClick={openConversation}
                underline="hover"
                sx={{ fontSize: 14, textTransform: 'uppercase', fontWeight: 500 }}
              >
                {formatMessage({ id: 'chat.toasts.documentLink', defaultMessage: 'Open document' })}
              </Link>
            ) : (
              <Button
                variant="text"
                onClick={openConversation}
                sx={{ p: 0, minWidth: 0, fontSize: 14, textTransform: 'uppercase', fontWeight: 500 }}
              >
                {formatMessage({ id: 'chat.toasts.openChat', defaultMessage: 'Open chat' })}
              </Button>
            )}
          </ListItemText>
        </ListItem>
      </List>
    </Box>
  );
};

export default ChatToast;
